import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";

interface Props {
  generation: Record<string, { Difference: number }>;
  analysisMode?: "ctg" | "ops";
}

const FUEL_COLORS: Record<string, string> = {
  SUN: "#FBBF24",
  WND: "#14B8A6",
  COL: "#6B7280",
  NG: "#F97316",
  NUC: "#A855F7",
  WAT: "#3B82F6",
  OIL: "#92400E",
  OTH: "#D1D5DB",
};

function getFuelColor(fuelType: string): string {
  if (FUEL_COLORS[fuelType]) return FUEL_COLORS[fuelType];
  const key = Object.keys(FUEL_COLORS).find((k) =>
    fuelType.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? FUEL_COLORS[key] : "#9CA3AF";
}

export function PortfolioGenerationChart({ generation, analysisMode = "ctg" }: Props) {
  const chartData = useMemo(() => {
    return Object.entries(generation)
      .map(([fuelType, values]) => ({
        fuelType: fuelType.replace(/_/g, " "),
        Difference: Number(values.Difference) || 0,
        rawFuel: fuelType,
      }))
      .filter((d) => Math.abs(d.Difference) > 0.001)
      .sort((a, b) => b.Difference - a.Difference);
  }, [generation]);

  if (chartData.length === 0) return null;

  const formatGwh = (v: number): string => {
    if (v === 0) return "0";
    const sign = v >= 0 ? "+" : "";
    if (Math.abs(v) >= 1) return `${sign}${Math.round(v)}`;
    return `${sign}${(v * 1000).toFixed(0)} MWh`;
  };

  return (
    <div className="rounded-xl bg-surface-700 border border-border p-6">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-lg font-semibold text-text-primary">
          Portfolio Generation Displacement
        </h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          analysisMode === "ops"
            ? "bg-amber-500/15 text-amber-400"
            : "bg-quantum-500/15 text-quantum-400"
        }`}>
          {analysisMode === "ops" ? "Operations" : "Cradle to Grave"}
        </span>
      </div>
      <p className="text-xs text-text-secondary mb-4">
        Aggregate change in grid generation by fuel type (GWh)
      </p>

      <ResponsiveContainer
        width="100%"
        height={Math.max(180, chartData.length * 40 + 40)}
      >
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#334155"
            horizontal={false}
          />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={{ stroke: "#334155" }}
            tickFormatter={(v) => formatGwh(v)}
            label={{
              value: "GWh",
              position: "insideBottomRight",
              offset: -5,
              style: { fill: "#64748b", fontSize: 11 },
            }}
          />
          <YAxis
            type="category"
            dataKey="fuelType"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={false}
            width={100}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e293b",
              border: "1px solid #334155",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#e2e8f0",
            }}
            formatter={(value: number) => [formatGwh(value), "Change"]}
          />
          <ReferenceLine x={0} stroke="#64748b" strokeWidth={1} />
          <Bar dataKey="Difference" barSize={20} radius={[0, 3, 3, 0]}>
            {chartData.map((entry) => (
              <Cell
                key={entry.rawFuel}
                fill={
                  entry.Difference >= 0
                    ? "#3B82F6"
                    : getFuelColor(entry.rawFuel)
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
